'use client';

import { useEffect, useState } from 'react';
import { Check, Copy, Mail, Send } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { appLink } from '@/lib/help/href';

type Props = {
  /** Anchor id, so the Inbox can send somebody back to this exact card. */
  id: string;
  to: string;
  subject: string;
  /**
   * Plain text, as a multi-line STRING attribute in the MDX (`body="…"`).
   * 🚨 Not `body={`…`}`: next-mdx-remote strips JavaScript expressions from
   * article props, so a template literal arrives as undefined. No double
   * quotes inside it.
   */
  body: string;
};

/**
 * An email somebody needs to send to a third party, written out for them.
 *
 * Three ways to send it, because a help page cannot tell who is reading it:
 * help.mucka.ai is a different origin from the app, so it has no session and
 * cannot ask whether this person has the Inbox connected. Copy works for
 * everybody, the mail-app link works for most, and the Inbox button is for the
 * people it applies to — the app decides that, not this page.
 *
 * 🚨 The Inbox button opens the compose sheet FILLED IN, never sends. The
 * owner reads it and presses Send themselves (trust model: nothing goes out
 * that the owner has not seen). An account without the Inbox lands back here
 * with `?inbox=unavailable` rather than on a 404.
 */
export default function HelpEmailTemplate({ id, to, subject, body }: Props) {
  const text = body.trim();
  const [copied, setCopied] = useState<'email' | 'address' | null>(null);
  // Read in an effect, not at render: the page is static and the query string
  // only exists in the browser.
  const [inboxUnavailable, setInboxUnavailable] = useState(false);
  useEffect(() => {
    setInboxUnavailable(new URLSearchParams(window.location.search).get('inbox') === 'unavailable');
  }, []);

  async function copy(what: 'email' | 'address') {
    try {
      await navigator.clipboard.writeText(what === 'email' ? text : to);
      setCopied(what);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard refused (an old browser, or an embedded webview). The text is
      // on screen and selectable, so there is nothing to report.
    }
  }

  const mailto = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
  function openInbox() {
    // `back` is where the app returns somebody whose account has no Inbox.
    // Read at click time: the path only exists in the browser.
    const back = `${window.location.pathname}#${id}`;
    window.location.href = appLink(
      `/inbox?${new URLSearchParams({ compose: '1', to, subject, body: text, back })}`,
    );
  }

  return (
    <section className="help-email" id={id}>
      <dl className="help-email-head">
        <dt>To</dt>
        <dd>
          {to}
          <Button variant="ghost" size="sm" onClick={() => copy('address')}>
            {copied === 'address' ? 'Copied' : 'Copy address'}
          </Button>
        </dd>
        <dt>Subject</dt>
        <dd>{subject}</dd>
      </dl>
      <pre className="help-email-body">{text}</pre>
      <div className="help-email-actions">
        <Button variant="primary" size="md" leadingIcon={copied === 'email' ? Check : Copy} trailingIcon={null} onClick={() => copy('email')}>
          {copied === 'email' ? 'Copied' : 'Copy email'}
        </Button>
        <Button variant="secondary" size="md" leadingIcon={Mail} onClick={() => { window.location.href = mailto; }}>
          Open in my email app
        </Button>
        <Button variant="secondary" size="md" leadingIcon={Send} onClick={openInbox}>
          Send from my Mucka inbox
        </Button>
      </div>
      {inboxUnavailable ? (
        <p className="help-email-note help-email-note--warn">
          Your email isn&rsquo;t connected to Mucka&rsquo;s Inbox yet, so copy the email above or open it in your email app instead.
        </p>
      ) : (
        <p className="help-email-note">
          Connected your email to Mucka&rsquo;s Inbox? The last button opens it ready to send, so you can read it through and press Send yourself.
        </p>
      )}
    </section>
  );
}
